<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

// 1:1-Port der Styleguide-Komponente `kgr-modal` (projects/styleguide/.../modal).
// Reine Shell: Backdrop + Dialog + Body-Scroll-Lock + Backdrop-Click + Blocking-Loading.
// Header/Content/Footer kommen als Slot-Inhalt vom Aufrufer (bzw. von KgrConfirmModal).
const props = withDefaults(
    defineProps<{
        open: boolean
        width?: string
        maxWidth?: string
        height?: string
        dialogClass?: string
        fullWidth?: boolean
        fullHeight?: boolean
        loading?: boolean
        closeOnBackdropClick?: boolean
        closeOnNavigation?: boolean
    }>(),
    {
        fullWidth: false,
        fullHeight: false,
        loading: false,
        closeOnBackdropClick: true,
        closeOnNavigation: false,
    },
);

const emit = defineEmits<{ close: [] }>();

const HTML_OPEN_CLASS = 'is-kgr-modal-open';

function setBodyScrollLock(locked: boolean) {
    document.documentElement.classList.toggle(HTML_OPEN_CLASS, locked);
}

function onBackdropClick(event: MouseEvent) {
    if (!props.closeOnBackdropClick || !props.open) {
        return;
    }
    // nur schließen, wenn direkt auf das Backdrop geklickt wurde (nicht auf den Dialog)
    if (event.target === event.currentTarget) {
        emit('close');
    }
}

const dialogRef = ref<HTMLElement | null>(null);

function focusContent() {
    const el = dialogRef.value;
    if (!el) {
        return;
    }
    const target = el.querySelector<HTMLElement>('[autofocus]') ?? el.querySelector<HTMLInputElement>('input');
    target?.focus();
}

watch(
    () => props.open,
    (open) => {
        setBodyScrollLock(open);
        if (open) {
            nextTick(focusContent);
        }
    },
    { immediate: true },
);

// closeOnNavigation: Modal bei Routenwechsel schließen (Angular: closeOnNavigation).
const router = useRouter();
watch(
    () => router.currentRoute.value.fullPath,
    () => {
        if (props.open && props.closeOnNavigation) {
            emit('close');
        }
    },
);

onBeforeUnmount(() => setBodyScrollLock(false));
</script>

<template>
    <Teleport to="body">
        <div
            v-if="open"
            class="kgr-modal is-open"
            :class="{ 'kgr-modal--fullWidth': fullWidth, 'kgr-modal--fullHeight': fullHeight }"
            @click="onBackdropClick"
        >
            <section
                ref="dialogRef"
                class="kgr-modal__dialog kgr-loadingContainer"
                :class="[dialogClass, { 'is-loading': loading, 'is-blocking': loading }]"
                :style="{ width, maxWidth, height }"
            >
                <div class="kgr-loadingContainer__loader">
                    <div class="kgr-loadingContainer__loadingIndicator kgr-loadingIndicator"></div>
                </div>
                <slot></slot>
            </section>
        </div>
    </Teleport>
</template>
